/**
 * useProgressSimulation Hook
 * 
 * Simulates progress percentage for operations where real progress isn't available.
 * Uses an easing curve to provide natural-feeling progress that slows near completion.
 * 
 * Features:
 * - Fast start, slow near end (ease-out curve)
 * - Lingers at 95-99% if operation takes longer than estimated
 * - Quickly animates to 100% when operation completes
 * - Smooth animation using requestAnimationFrame
 */

import { useState, useEffect, useRef } from 'react';

// Estimated durations in seconds for different operation types
export const ESTIMATED_DURATIONS: Record<string, number> = {
  text: 15,      // 15 seconds for text generation
  image: 60,     // 1 minute for images
  video: 300,    // 5 minutes for videos
  v2v: 600,      // 10 minutes for video-to-video (Wan Animate, etc.)
};

/**
 * Easing function - fast start, slow near end
 * Uses a modified ease-out curve that asymptotically approaches 95%
 * at the estimated duration time
 */
function easeOutProgress(elapsed: number, duration: number): number {
  const t = Math.min(elapsed / duration, 1);
  
  // Modified ease-out that reaches ~95% at t=1
  // Formula: 1 - (1-t)^2, scaled to 95%
  const eased = 1 - Math.pow(1 - t, 2);
  return eased * 95;
}

/**
 * Slow crawl from 95% to 99% when operation exceeds estimate
 */
function slowCrawlProgress(elapsed: number, duration: number): number {
  const overtime = elapsed - duration;
  // Take 60 seconds to crawl from 95% to 99%
  const crawlDuration = 60;
  const crawlProgress = Math.min(overtime / crawlDuration, 1);
  return 95 + (crawlProgress * 4); // 95% -> 99%
}

interface UseProgressSimulationOptions {
  isProcessing: boolean;
  estimatedDuration?: number; // in seconds
  operationType?: string; // 'text' | 'image' | 'video' or any string
  onComplete?: () => void;
}

interface UseProgressSimulationResult {
  progress: number;
  isAnimating: boolean;
  completeProgress: () => void;
}

export function useProgressSimulation({
  isProcessing,
  estimatedDuration,
  operationType = 'text',
  onComplete,
}: UseProgressSimulationOptions): UseProgressSimulationResult {
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const startTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>(0);
  const isCompletingRef = useRef<boolean>(false);
  const progressRef = useRef<number>(0);
  const onCompleteRef = useRef(onComplete);
  
  // Keep onComplete ref updated
  onCompleteRef.current = onComplete;
  
  // Determine duration to use
  const duration = estimatedDuration ?? ESTIMATED_DURATIONS[operationType] ?? 30;
  const durationRef = useRef(duration);
  durationRef.current = duration;
  
  // Animation function - defined outside effect so it can be reused
  const animateRef = useRef<() => void>(() => {});
  
  animateRef.current = () => {
    const now = performance.now();
    const elapsed = (now - startTimeRef.current) / 1000; // Convert to seconds
    
    if (isCompletingRef.current) {
      // Quick animation to 100%
      const remainingProgress = 100 - progressRef.current;
      const step = Math.max(remainingProgress * 0.15, 1); // Animate 15% of remaining per frame, min 1
      const newProgress = Math.min(progressRef.current + step, 100);
      
      progressRef.current = newProgress;
      setProgress(Math.round(newProgress));
      
      if (newProgress >= 100) {
        setIsAnimating(false);
        isCompletingRef.current = false;
        onCompleteRef.current?.();
        return; // Stop animation
      }
    } else {
      // Normal progress simulation
      let newProgress: number;
      
      if (elapsed <= durationRef.current) {
        // Normal easing toward 95%
        newProgress = easeOutProgress(elapsed, durationRef.current);
      } else {
        // Slow crawl from 95% to 99%
        newProgress = slowCrawlProgress(elapsed, durationRef.current);
      }
      
      progressRef.current = newProgress;
      setProgress(Math.round(newProgress));
    }
    
    animationFrameRef.current = requestAnimationFrame(animateRef.current);
  };
  
  // Start animation when processing begins
  useEffect(() => {
    if (isProcessing) {
      // Start fresh
      startTimeRef.current = performance.now();
      progressRef.current = 0;
      isCompletingRef.current = false;
      setProgress(0);
      setIsAnimating(true);
      
      animationFrameRef.current = requestAnimationFrame(animateRef.current);
    }
    
    return () => {
      // Only cancel if we're not completing
      if (animationFrameRef.current && !isCompletingRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isProcessing]);
  
  // Handle completion animation when processing stops
  useEffect(() => {
    if (!isProcessing && isAnimating && !isCompletingRef.current) {
      // Processing stopped - trigger completion animation
      isCompletingRef.current = true;
      animationFrameRef.current = requestAnimationFrame(animateRef.current);
    }
  }, [isProcessing, isAnimating]);
  
  // Manual complete function (for when we know operation is done)
  const completeProgress = () => {
    if (isAnimating) {
      isCompletingRef.current = true;
    }
  };
  
  return {
    progress,
    isAnimating,
    completeProgress,
  };
}

export default useProgressSimulation;
